import { FC, useState } from 'react';
import { DateRange } from 'react-day-picker';
import ModalComponent from '../modal/modal';
import {
  renderFormRow,
  userStatusOption,
  userTypeOptions,
  venueStatusOption,
} from './helper';
import { Trans } from 'react-i18next';
import DatePicker from '../datePicker/datePicker';
import DropDownInput from '../dropDownInput/dropDownInput';
import { UserFilterProps, UserManagementFilters } from './type';
import { useOrganizationContext } from '../../service/context/organizationContext';
import { OrganizationUnitType, UserStatus, UserType } from '@inplay/models';
import { Option } from '../dropDownInput/dropDownInput.type';
import FilterIcon from '../../assets/filter.png';
import { OrgUnit } from '../../utils/helper.type';
import { OrganisationToggleEnum } from '../../pages/userManagement/userManagement.type';
import { ViewByOption } from '../../pages/userManagement/venuesView/venuesView.type';
import { Mode } from '../datePicker/datePicker.type';

export const BaseUserFilter: UserManagementFilters = {
  //  Filters applied for all organization unit
  userType: [],
  userStatus: [],
  venueBranches: [],
  dateRange: { from: undefined, to: undefined },
  //  View filter — used **only for venues**
  selectedViewBy: [],
};

export const UserManagementFilter: FC<UserFilterProps> = (props) => {
  const [filters, setFilters] = useState<UserManagementFilters>(props.filters);
  const { organizationSummary } = useOrganizationContext();

  const handleClear = () => {
    setFilters(BaseUserFilter);
    props.setFilters(BaseUserFilter);
    props.setFilterModalOpen(false);
  };

  const venueBranchOption: Option[] =
    organizationSummary
      ?.filter((org: OrgUnit) => org.type === OrganizationUnitType.VENUE_BRANCH)
      .map((org: OrgUnit) => ({
        label: org.name,
        value: org._id,
      })) || [];

  const handleApply = () => {
    props.setFilters(filters);
    props.setFilterModalOpen(false);
  };

  return (
    <>
      <div
        onClick={() => props.setFilterModalOpen((prev: boolean) => !prev)}
        className="filter-container mr-3  mt-1 relative inline-block cursor-pointer"
      >
        <img src={FilterIcon} alt="Settings" className="w-3 h-3" />
      </div>
      <ModalComponent
        open={props.isOpenUserModal}
        shouldCloseOnOutsideClick={true}
        onToggle={() => {}}
        onFooterButton1Click={handleClear}
        onFooterButton2Click={handleApply}
        dialogClassName={'top-[190px] w-[600px]'}
        title={<Trans i18nKey="userManagement.userFilter.filter" />}
        footerButton1Text={
          <Trans i18nKey="userManagement.userFilter.clearAll" />
        }
        footerButton2Text={<Trans i18nKey="userManagement.userFilter.apply" />}
        onClose={() => props.setFilterModalOpen(false)}
        showFooter2Button
        showFooter1Button
      >
        <div className="z-20">
          {renderFormRow(
            <Trans i18nKey="userManagement.userFilter.addedOn" />,
            <DatePicker
              mode={Mode.Range}
              handleApply={(range: DateRange) =>
                setFilters((prev: UserManagementFilters) => ({
                  ...prev,
                  dateRange: range,
                }))
              }
              finalRange={filters.dateRange}
              className={'top-[340px]'}
            />,
          )}
        </div>

        {renderFormRow(
          <Trans i18nKey="userManagement.userFilter.status" />,
          <DropDownInput
            placeholder={<Trans i18nKey={'userManagement.select'} />}
            options={
              props.selectedOrganisationToggle !==
                OrganisationToggleEnum.VENUES &&
              props.selectedOrganisationToggle !==
                OrganisationToggleEnum.ORGANISATION
                ? userStatusOption
                : venueStatusOption
            }
            isMulti
            selected={filters.userStatus}
            onSelect={(selected: string | string[]) => {
              if (Array.isArray(selected)) {
                setFilters((prev: UserManagementFilters) => ({
                  ...prev,
                  userStatus: selected as UserStatus[],
                }));
              } else {
                setFilters((prev: UserManagementFilters) => ({
                  ...prev,
                  userStatus: [selected as UserStatus],
                }));
              }
            }}
          />,
        )}
        {props.selectedOrganisationToggle !== OrganisationToggleEnum.VENUES &&
          props.selectedOrganisationToggle !==
            OrganisationToggleEnum.ORGANISATION &&
          renderFormRow(
            <Trans i18nKey="userManagement.userFilter.userType" />,
            <DropDownInput
              placeholder={<Trans i18nKey={'userManagement.select'} />}
              isMulti
              options={userTypeOptions}
              selected={filters.userType}
              onSelect={(selected: string | string[]) => {
                if (Array.isArray(selected)) {
                  setFilters((prev: UserManagementFilters) => ({
                    ...prev,
                    userType: selected as UserType[],
                  }));
                } else {
                  setFilters((prev: UserManagementFilters) => ({
                    ...prev,
                    userType: [selected as UserType],
                  }));
                }
              }}
            />,
          )}

        {props.selectedOrganisationToggle !== OrganisationToggleEnum.VENUES &&
          props.selectedOrganisationToggle !==
            OrganisationToggleEnum.ORGANISATION &&
          renderFormRow(
            <Trans i18nKey="userManagement.userFilter.venueBranch" />,
            <DropDownInput
              placeholder={<Trans i18nKey={'userManagement.select'} />}
              isMulti
              options={venueBranchOption}
              selected={filters.venueBranches}
              onSelect={(selected: string | string[]) => {
                setFilters((prev: UserManagementFilters) => ({
                  ...prev,
                  venueBranches: Array.isArray(selected)
                    ? (selected as string[])
                    : [selected as string],
                }));
              }}
            />,
          )}

        {(props.selectedOrganisationToggle === OrganisationToggleEnum.VENUES ||
          props.selectedOrganisationToggle ===
            OrganisationToggleEnum.ORGANISATION) &&
          renderFormRow(
            <Trans i18nKey="userManagement.userFilter.viewBy" />,
            <DropDownInput
              placeholder={<Trans i18nKey="userManagement.select" />}
              options={props.viewByOptions ?? []}
              isMulti
              selected={filters.selectedViewBy}
              onSelect={(selected: ViewByOption[] | ViewByOption) => {
                setFilters((prev) => ({
                  ...prev,
                  selectedViewBy: Array.isArray(selected)
                    ? selected
                    : [selected],
                }));
              }}
            />,
          )}
      </ModalComponent>
    </>
  );
};
